<template>
  <div class="query-upload">
    <form @submit.prevent="searchPaperBeforeSubmitMethod">
      <!-- Search Input Section -->
      <div class="query-upload__form-group">
        <label for="query-search-input" class="query-upload__label">
          Search research papers across the web by title, keywords, or topic
        </label>
        <div class="query-upload__search-wrapper">
          <input
            id="query-search-input"
            v-model.trim="searchTerm"
            v-validate="'required'"
            type="text"
            name="search_term"
            data-vv-as="search query"
            autocomplete="off"
            placeholder="e.g. Quantum Computing, Neural Networks..."
            class="query-upload__input"
            :class="{ 'query-upload__input--invalid': errors.has('search_term') }"
          />
          <button
            type="submit"
            class="query-upload__search-btn"
            :class="{ 'query-upload__search-btn--active': searchTerm.length > 0 }"
            :disabled="loader"
            aria-label="Search research papers"
          >
            <i class="fa fa-search" aria-hidden="true"></i>
          </button>
        </div>
        <small v-if="errors.has('search_term')" class="query-upload__error">
          {{ errors.first('search_term') }}
        </small>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loader" class="query-upload__spinner-wrapper">
        <div class="query-upload__spinner"></div>
      </div>

      <!-- Search Results List -->
      <div v-if="hasSearched && !loader" class="query-upload__results">
        <div v-if="searchResultPapers.length > 0" class="query-upload__list">
          <article
            v-for="(item, index) in searchResultPapers"
            :key="item.id || index"
            class="query-upload__card"
          >
            <h2 class="query-upload__card-title">
              <a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ item.title || 'Untitled Paper' }}
              </a>
              <span v-else>{{ item.title || 'Untitled Paper' }}</span>
            </h2>

            <p v-if="item.authors" class="query-upload__card-author">
              {{ item.authors }}
            </p>

            <button
              type="button"
              class="query-upload__btn-save"
              @click="savePaperMethod(item)"
            >
              <i class="fa fa-bookmark-o" aria-hidden="true"></i>
              <span>Save</span>
            </button>
          </article>
        </div>

        <!-- No Results Fallback -->
        <div v-else class="query-upload__no-results">
          <i class="fa fa-info-circle query-upload__no-results-icon" aria-hidden="true"></i>
          <p>No research papers found for "{{ searchTerm }}". Please try different keywords.</p>
        </div>
      </div>
    </form>

    <!-- Save / Update Paper Details Modal -->
    <save-paper-popup
      v-if="savePaperPopupState"
      :paper-info="paperUpdateInfo"
    ></save-paper-popup>
  </div>
</template>

<script>
import axios from 'axios';
import { bus } from '@/main';
import root from '@/_helpers/roots';
import toast from '@/services/toast';
import savePaperPopup from '../../save-paper-info';

const paperUploadFromQuery = root.uploadPDFFromQueryApiUrl.link;

export default {
  name: 'AddPaperFromQuery',
  components: {
    'save-paper-popup': savePaperPopup
  },
  data() {
    return {
      searchTerm: '',
      savePaperPopupState: false,
      paperUpdateInfo: null,
      searchResultPapers: [],
      loader: false,
      hasSearched: false
    };
  },
  mounted() {
    bus.$on('savePaperPopupStateBus', (data) => {
      this.savePaperPopupState = data;
    });
  },
  beforeDestroy() {
    bus.$off('savePaperPopupStateBus');
  },
  methods: {
    savePaperMethod(data) {
      this.paperUpdateInfo = data;
      this.savePaperPopupState = true;
      document.body.classList.add('body_right_scroll_remove');
    },

    async searchPaperBeforeSubmitMethod() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      this.searchResultPapers = [];
      this.loader = true;
      this.hasSearched = false;

      try {
        const res = await axios.get(paperUploadFromQuery, {
          params: { query: this.searchTerm }
        });

        if (res.data && res.data.data && Array.isArray(res.data.data.result)) {
          this.searchResultPapers = res.data.data.result;
        } else {
          this.searchResultPapers = [];
        }
        this.hasSearched = true;
      } catch (err) {
        this.hasSearched = true;
        const errorMsg =
          (err.response && err.response.data && (err.response.data.user_msg || err.response.data.msg)) ||
          (err.data && (err.data.user_msg || err.data.msg)) ||
          'Failed to retrieve search results';
        toast.error(errorMsg);
      } finally {
        this.loader = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$theme-gold: #ffd400;
$accent-blue: #87cefa;
$border-translucent: rgba(255, 255, 255, 0.1);
$bg-translucent: rgba(255, 255, 255, 0.03);
$error-color: #dc3545;
$text-light: #ffffff;
$text-muted: #d6d6d6;

.query-upload {
  letter-spacing: 0.5px;
  width: 100%;

  &__form-group {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 1rem;
    color: $text-light;
    margin-bottom: 8px;
    font-weight: 500;
    line-height: 1.4;
  }

  &__search-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__input {
    width: 100%;
    height: 48px;
    padding: 10px 48px 10px 16px;
    font-size: 1rem;
    color: $theme-gold;
    background: $bg-translucent;
    border: 1px solid $border-translucent;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      border-color: $theme-gold;

      & ~ .query-upload__search-btn {
        color: $theme-gold;
      }
    }

    &--invalid {
      border-color: $error-color !important;
    }
  }

  &__search-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    font-size: 1.1rem;
    cursor: pointer;
    padding: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease, transform 0.15s ease;

    &--active {
      color: $theme-gold;

      &:hover {
        color: $accent-blue;
        transform: translateY(-50%) scale(1.1);
      }
    }

    &:focus-visible {
      outline: 2px solid $theme-gold;
      border-radius: 4px;
    }
  }

  &__error {
    color: $error-color;
    font-size: 0.875rem;
    margin-top: 6px;
  }

  // Loading Spinner
  &__spinner-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px 0;
  }

  &__spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.15);
    border-top-color: $theme-gold;
    border-radius: 50%;
    animation: query-spin 0.8s linear infinite;
  }

  // Results Section
  &__results {
    margin-top: 20px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__card {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__card-title {
    font-size: 1.15rem;
    line-height: 1.4;
    margin: 0 0 8px;
    font-weight: 600;

    a {
      color: $accent-blue;
      text-decoration: none;
      transition: color 0.2s ease;

      &:hover {
        color: $theme-gold;
        text-decoration: underline;
      }
    }
  }

  &__card-author {
    font-size: 0.95rem;
    font-family: 'Georgia', serif;
    color: $text-muted;
    line-height: 1.4;
    margin: 0 0 16px;
  }

  &__btn-save {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 18px;
    background: rgba(255, 255, 255, 0.1);
    color: $theme-gold;
    border: 1px solid rgba(255, 212, 0, 0.3);
    border-radius: 4px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $accent-blue;
      border-color: $accent-blue;
      color: #1a1a1a;
    }
  }

  &__no-results {
    text-align: center;
    padding: 32px 16px;
    color: $text-muted;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;

    &-icon {
      font-size: 1.75rem;
      color: rgba(255, 255, 255, 0.3);
    }

    p {
      margin: 0;
      font-size: 0.95rem;
    }
  }
}

@keyframes query-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>